import { Col, Icon, Row, Spin } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { ApplicationContext } from "../context";
import { BrandType } from "../types/enumerations/common";
import { Brand, Onload } from "../types/interfaces/onload";
import { DataUtils } from "../utilities";
import { getAuthenticationDetails } from "../utilities/authentication";

declare global {
    interface Window {
        ConfigToolApp: any
    }
}

enum UserType {
    CHANNEL = "CHANNEL",
    COBRAND = "COBRAND",
    ADMIN = "ADMIN"
}

enum DestinationBrandType {
    CHANNEL = "CHANNEL",
    COBRAND = "COBRAND",
    SUB_BRAND = "SUB_BRAND"
}

type IConfigToolProps = {
    rsession: string,
    yccUrl: string,
    brandId: string,
    appId: string,
    brandName: string,
    locationUrl: string,
    region: string,
    environment: string,
    userName: string,
    userType?: string,
    destinationCobrandId?: string | undefined,
    destinationCobrandType?: string | undefined,
    destinationChannelId?: string | undefined,
    app?: string,
    flow?: string,
    extraParams?: string
}

type Token = {
    appId: string,
    brandId: number,
    brandName: string,
    environment: string,
    region: string,
    rsession: string,
    yccUrl: string,
    supportingChannelId?: number
}

interface IProps {
    brand: Brand & { channel?: Brand },
    app: "onboard" | "product" | "management" | "siteMetadata",
    flow?: "obApp" | "managementApp",
    extraParams?: string
    iFrame: {
        id: string,
        name: string
    },
    onDataChanged?: (isChanged: boolean, extraParams?: string) => void,
    onBrandChange?: (brand: Brand & { channel?: Brand }, extraParams?: string) => void,
    redirectToManageProvider?: (brand: Brand & { channel?: Brand }, extraParams?: string) => void,
    redirectToProduct?: (brand: Brand & { channel?: Brand }, extraParams?: string, flow?: "obApp") => void,
    redirectToOnboard?: (brand: Brand & { channel?: Brand }, extraParams?: string) => void,
    redirectToPermission?: (brand: Brand & { channel?: Brand }) => void,
}


const postForm = (path: string, params: any, method = "POST", target: string = "_blank") => {
    const form = document.createElement('form');
    form.method = method;
    form.action = path;
    form.target = target;
    for (const key in params) {
        if (params.hasOwnProperty(key)) {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = key;
            hiddenField.value = params[key];
            form.appendChild(hiddenField);
        }
    }
    document.body.appendChild(form);
    form.submit();
}

const ConfigTool: React.FC<IProps> = ({ brand, flow, app, extraParams, iFrame, onDataChanged, redirectToManageProvider, onBrandChange, redirectToProduct, redirectToOnboard, redirectToPermission }) => {
    const { permissions, onload } = useContext(ApplicationContext);

    const [isFrameLoading, setIsFrameLoading] = useState<boolean>(true);
    const [token, setToken] = useState<Token>();

    const loadConfigToolScript = (configToolUrl: string) => {
        if (!window.ConfigToolApp) {
            const urlOrigin = new URL(configToolUrl)?.origin;
            const scriptUrl = urlOrigin + "/js/post-message.js"
            const script = document.createElement("script");
            script.src = scriptUrl;
            script.onload = () => {
                if (window.ConfigToolApp) {
                    window.ConfigToolApp.initialize({
                        "name": "myyodlee",
                        "app": app,
                        "attributes": {
                            "iframeId": iFrame.id
                        },
                        "eventHandlers": {
                            "error": function (eventData: any) {
                            },
                            "renew_session": function (eventData: any) {
                            }
                        }
                    })
                }
            }
            document.head.appendChild(script);
        } else {
            window.ConfigToolApp.initialize({
                "name": "myyodlee",
                "app": app,
                "attributes": {
                    "iframeId": iFrame.id
                },
                "eventHandlers": {
                    "error": function (eventData: any) {
                    },
                    "renew_session": function (eventData: any) {
                    }
                }
            })
        }
    }

    const loadConfigTool = () => {
        if (onload?.configToolApiUrl) {
            const isAllCustomersSelected = [BrandType.YODLEE, BrandType.CHANNEL].includes(onload.financialInstitution?.type?.id) && !brand?.id;

            if ((!isAllCustomersSelected || brand) && token) {
                setIsFrameLoading(true);
                loadConfigToolScript(onload?.configToolApiUrl);

                let props: IConfigToolProps = {
                    rsession: token.rsession,
                    yccUrl: token.yccUrl,
                    brandId: token.brandId + "",
                    appId: token.appId,
                    brandName: token?.brandName,
                    locationUrl: window.origin,
                    region: token?.region,
                    environment: token?.environment,
                    userName: onload?.user?.userName,

                };

                if (brand.id) {
                    const brandType = onload?.financialInstitution.type.id;

                    //setting destination channel ID
                    if (brandType === BrandType.SUB_BRAND) {
                        props.destinationChannelId = token?.supportingChannelId?.toString() || ""
                    } else if (brand?.type?.id === BrandType.SUB_BRAND) {
                        if (brand?.channel?.id) {
                            props.destinationChannelId = brand.channel?.id?.toString() || "";
                        }
                        else {
                            props.destinationChannelId = getChannelForSubBrand(brand?.id)?.id?.toString() || ""
                        }
                    }

                    //setting user
                    const selfSelected = onload.financialInstitution.id === brand?.id
                    if (brandType === BrandType.CHANNEL && !selfSelected) {
                        props.userType = UserType.CHANNEL;
                    } else if (brandType === BrandType.YODLEE && !selfSelected) {
                        props.userType = UserType.ADMIN;
                    } else {
                        props.userType = UserType.COBRAND;
                    }

                    switch (brand?.type.id) {
                        case BrandType.CHANNEL:
                            props.destinationCobrandType = DestinationBrandType.CHANNEL;
                            break;
                        case BrandType.SUB_BRAND:
                            props.destinationCobrandType = DestinationBrandType.SUB_BRAND;
                            break;
                        default:
                            props.destinationCobrandType = DestinationBrandType.COBRAND;
                    }

                    props.destinationCobrandId = brand.id;
                }

                if (app === "onboard") {
                    props.app = "onboard";
                } else if (app === "siteMetadata") {
                    props.app = "siteMetadata";
                }

                if (flow) {
                    props.flow = flow;
                }
                if (extraParams) {
                    props.extraParams = extraParams;
                }

                postForm(onload.configToolApiUrl, props, "POST", iFrame.name);
            }
        }
    }

    const findBrandById = (onload: Onload, brandId: number) => {
        if (onload.financialInstitution?.id === brandId) {
            return onload.financialInstitution;
        }

        let returnBrand: Brand | undefined = undefined;
        onload.financialInstitution?.brands?.find(brand => {
            if (brand.id === brandId) {
                returnBrand = brand;
                return true;
            }
            else if (brand.type?.id === BrandType.CHANNEL) {
                const foundSubBrand = brand.brands?.find(subBrand => {
                    if (subBrand.id === brandId) {
                        returnBrand = subBrand;
                        return true;
                    }
                })
                if (foundSubBrand) {
                    return true;
                }
            }
        });

        return returnBrand;
    }

    const getChannelForSubBrand = (brandId: number): Brand | undefined => {
        const brandType = onload?.financialInstitution.type.id;
        if (brandType === BrandType.YODLEE) {
            let channel: Brand | undefined = undefined;
            onload?.financialInstitution?.brands?.find(brand => {
                if (brand.type.id === BrandType.CHANNEL) {
                    const foundSubBrand = brand.brands?.find(subBrand => {
                        if (subBrand.id === brandId) {
                            channel = brand;
                            return true;
                        }
                    })
                    if (foundSubBrand) {
                        return true;
                    }
                }
            });
            return channel;
        } else if (brandType === BrandType.CHANNEL && onload?.financialInstitution.id !== brandId) {
            return onload?.financialInstitution as Brand;
        } else {
            return undefined;
        }
    }

    useEffect(() => {
        const authentication = getAuthenticationDetails(brand);

        DataUtils.configToolToken(authentication).get().then((token) => {
            setToken(token);
        });

        const messageHandler = (event: any) => {
            if (event?.data?.eventName === "renew_session") {
                window.dispatchEvent(new Event('resetTimer'));
            }
            else if (event?.data?.eventName === "error") {
                window.dispatchEvent(new Event('unauthorisedAccess'));
            }
            else if (event?.data?.eventName === "config_data_change") {
                if (onDataChanged) {
                    const changeState: boolean = event?.data?.eventData?.changed;
                    onDataChanged(changeState);
                }
            }
            //TODO: Temporary 
            else if (event?.data?.eventName === "parent_redirect") {
                if (redirectToManageProvider) {
                    const brandId = parseInt(event?.data?.eventData?.destinationCobrandId);
                    const brand = onload && findBrandById(onload, brandId);

                    redirectToManageProvider({
                        id: brand ? brand.id : brandId,
                        name: brand ? brand.name : "N/A",
                        type: brand ? brand.type : { id: BrandType.COBRAND, name: "COBRAND" },
                        brands: brand?.brands || [],
                        channel: getChannelForSubBrand(brandId)
                    }, event?.data?.eventData?.extraParams);
                }
            }
            else if (event?.data?.eventName === "redirect_action") {
                if (event?.data?.eventData?.name === "OBAPP") {
                    if (redirectToProduct) {
                        const brandId = parseInt(event?.data?.eventData?.data?.destinationCobrandId);
                        const brand = onload && findBrandById(onload, brandId);

                        redirectToProduct({
                            id: brand ? brand.id : brandId,
                            name: brand ? brand.name : "N/A",
                            type: brand ? brand.type : { id: BrandType.COBRAND, name: "COBRAND" },
                            brands: brand?.brands || [],
                            channel: getChannelForSubBrand(brandId)
                        }, event?.data?.eventData?.data?.extraParams, "obApp");
                    }
                } else if (event?.data?.eventData?.name === "PRODUCTS") {
                    if (redirectToProduct) {
                        const brandId = parseInt(event?.data?.eventData?.data?.destinationCobrandId);
                        const brand = onload && findBrandById(onload, brandId);

                        redirectToProduct({
                            id: brand ? brand.id : brandId,
                            name: brand ? brand.name : "N/A",
                            type: brand ? brand.type : { id: BrandType.COBRAND, name: "COBRAND" },
                            brands: brand?.brands || [],
                            channel: getChannelForSubBrand(brandId)
                        }, event?.data?.eventData?.data?.extraParams);
                    }
                }
                else if (event?.data?.eventData?.name === "MANAGE_PROVIDERS") {
                    if (redirectToManageProvider) {
                        const brandId = parseInt(event?.data?.eventData?.data?.destinationCobrandId);
                        const brand = onload && findBrandById(onload, brandId);

                        redirectToManageProvider({
                            id: brand ? brand.id : brandId,
                            name: brand ? brand.name : "N/A",
                            type: brand ? brand.type : { id: BrandType.COBRAND, name: "COBRAND" },
                            brands: brand?.brands || [],
                            channel: getChannelForSubBrand(brandId)
                        }, event?.data?.eventData?.data?.extraParams);
                    }
                } else if (event?.data?.eventData?.name === "ONBOARD") {
                    if (redirectToOnboard) {
                        const brandId = parseInt(event?.data?.eventData?.data?.destinationCobrandId);
                        const brand = onload && findBrandById(onload, brandId);

                        redirectToOnboard({
                            id: brand ? brand.id : brandId,
                            name: brand ? brand.name : "N/A",
                            type: brand ? brand.type : { id: BrandType.COBRAND, name: "COBRAND" },
                            brands: brand?.brands || [],
                            channel: getChannelForSubBrand(brandId)
                        }, event?.data?.eventData?.data?.extraParams);
                    }
                }
            } else if (event?.data?.eventName === "manage_user_redirect") {
                if (redirectToPermission) {
                    const brandId = parseInt(event?.data?.eventData?.destinationCobrandId);
                    const brand = onload && findBrandById(onload, brandId);

                    redirectToPermission({
                        id: brand ? brand.id : brandId,
                        name: brand ? brand.name : "N/A",
                        type: brand ? brand.type : { id: BrandType.COBRAND, name: "COBRAND" },
                        brands: brand?.brands || [],
                        channel: getChannelForSubBrand(brandId)
                    });
                }
            } else if (event?.data?.eventName === "tenant_change") {
                if (onBrandChange) {
                    const brandId = parseInt(event?.data?.eventData?.destinationCobrandId);
                    const brand = onload && findBrandById(onload, brandId);

                    onBrandChange({
                        id: brand ? brand.id : brandId,
                        name: brand ? brand.name : "N/A",
                        type: brand ? brand.type : { id: BrandType.COBRAND, name: "COBRAND" },
                        brands: brand?.brands || [],
                        channel: getChannelForSubBrand(brandId)
                    }, event?.data?.eventData?.extraParams);
                }
            }
        }

        window.addEventListener("message", messageHandler);

        return () => window.removeEventListener("message", messageHandler);
    }, []);

    useEffect(() => {
        if (token) {
            loadConfigTool();
        }
    }, [token, brand.id, extraParams]);

    return (
        onload && permissions && token ?
            <Spin
                size="large"
                indicator={<Icon type="loading" />}
                spinning={isFrameLoading}
                wrapperClassName="spinner-config-tool"
            >
                <iframe
                    width="100%"
                    height="450"
                    marginHeight={0}
                    marginWidth={0}
                    frameBorder={0}
                    title="Config tool container"
                    scrolling="no"
                    id={iFrame.id}
                    name={iFrame.name}
                    onLoad={() => setIsFrameLoading(false)}
                />
            </Spin> :
            <Row type="flex" style={{ height: "80vh" }} justify="center" align="middle">
                <Col>
                    <Spin size="large" indicator={<Icon type="loading" />} />
                </Col>
            </Row>
    )
}

export default ConfigTool;